import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DesktopAlertContext } from './DesktopAlertContext';
import { MobileAlertContext } from '../../../Mobile/common/alerts/MobileAlertContext';
import DesktopAlertStack from './DesktopAlertStack';
import useLanguage from '../../../../hooks/useLanguage';

const normalizeActions = (actions, alertId, defaultLabel) => {
  if (!Array.isArray(actions)) {
    return [];
  }

  return actions.map((action, index) => ({
    id: `${alertId}-action-${index}`,
    label: action?.label ?? defaultLabel,
    onClick: typeof action?.onClick === 'function' ? action.onClick : null,
    variant: action?.variant || 'secondary',
    dismissOnClick: action?.dismissOnClick !== false
  }));
};

export const DesktopAlertProvider = ({ children }) => {
  const { t } = useLanguage();
  const idCounterRef = useRef(0);
  const [alerts, setAlerts] = useState([]);
  const defaultActionLabel = t('common.actions.action', { defaultValue: 'Action' });

  const dismiss = useCallback((id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clear = useCallback(() => {
    setAlerts([]);
  }, []);

  const notify = useCallback((options = {}) => {
    const id = `desktop-alert-${Date.now()}-${idCounterRef.current++}`;
    setAlerts((prev) => [
      ...prev,
      {
        id,
        variant: options.variant || 'info',
        title: options.title || '',
        description: options.description || '',
        dismissible: options.dismissible !== false,
        duration: typeof options.duration === 'number' ? options.duration : null,
        actions: normalizeActions(options.actions, id, defaultActionLabel)
      }
    ]);

    return id;
  }, [defaultActionLabel]);

  const confirm = useCallback((options = {}) => {
    const {
      title = '',
      description = '',
      confirmText = t('common.actions.confirm', { defaultValue: 'Conferma' }),
      cancelText = t('common.actions.cancel', { defaultValue: 'Annulla' }),
      variant = 'warning',
      onConfirm,
      onCancel
    } = options;

    return new Promise((resolve) => {
      let alertId = null;

      const handleResult = (result, callback) => {
        if (typeof callback === 'function') {
          callback();
        }

        resolve(result);

        if (alertId) {
          dismiss(alertId);
        }
      };

      alertId = notify({
        variant,
        title,
        description,
        dismissible: false,
        actions: [
          {
            label: cancelText,
            variant: 'ghost',
            onClick: () => handleResult(false, onCancel)
          },
          {
            label: confirmText,
            variant: 'primary',
            onClick: () => handleResult(true, onConfirm)
          }
        ]
      });
    });
  }, [dismiss, notify, t]);

  const value = useMemo(() => ({
    notify,
    dismiss,
    clear,
    confirm
  }), [notify, dismiss, clear, confirm]);

  return (
    <DesktopAlertContext.Provider value={value}>
      <MobileAlertContext.Provider value={value}>
        {children}
        <DesktopAlertStack alerts={alerts} onDismiss={dismiss} />
      </MobileAlertContext.Provider>
    </DesktopAlertContext.Provider>
  );
};
